import React, { memo, useRef, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FormLabel } from "../FormLabel";
import FormikFieldError from "../FormikFieldError";
import InfoTooltip from "newui/components/InfoTooltip";
import {
	getInputWrapperClasses,
	baseInputClasses,
	normalInputClasses,
	errorInputClasses,
} from "../InputWithLabel/inputStyles";
import "./rich-text-editor.css";

export interface RichTextEditorProps {
	className?: string;
	disabled?: boolean;
	errorText?: string;
	guideLine?: string;
	hasError?: boolean;
	label?: string;
	minHeight?: string;
	noMargin?: boolean;
	placeholder?: string;
	required?: boolean;
	testId?: string;
	value: string;
	onChange: (html: string) => void;
}

const MODULES = {
	toolbar: [
		[{ header: [1, 2, 3, false] }],
		[{ size: ["small", false, "large", "huge"] }],
		["bold", "italic", "underline"],
		[{ color: [] }, { background: [] }],
		[{ list: "ordered" }, { list: "bullet" }],
	],
};

const RichTextEditorInner: React.FC<RichTextEditorProps> = ({
	className = "",
	disabled = false,
	errorText,
	guideLine,
	hasError = false,
	label,
	minHeight = "6rem",
	noMargin,
	placeholder = "Enter text...",
	required,
	testId,
	value,
	onChange,
}) => {
	const hasInputError = !!errorText || hasError;
	const quillRef = useRef<ReactQuill>(null);

	const handleChange = useCallback(
		(content: string) => {
			const isEmpty = !content || content === "<p><br></p>";
			onChange(isEmpty ? "" : content);
		},
		[onChange]
	);

	return (
		<div
			className={getInputWrapperClasses(hasInputError, noMargin, className)}
			data-testid={testId}
		>
			{label && (
				<FormLabel label={label} required={required}>
					{guideLine && (
						<div className="inline-block">
							<InfoTooltip tooltipText={guideLine} />
						</div>
					)}
				</FormLabel>
			)}

			<div
				className={`
					${baseInputClasses}
					${hasInputError ? errorInputClasses : normalInputClasses}
					rich-text-editor-wrapper !p-0
					${disabled ? "cursor-not-allowed bg-gray-300" : ""}
				`}
				style={{ minHeight }}
			>
				<ReactQuill
					ref={quillRef}
					value={value}
					onChange={handleChange}
					modules={MODULES}
					placeholder={placeholder}
					readOnly={disabled}
					className="rich-text-editor-quill font-roboto text-sm [&_.ql-blank::before]:!text-gray-400"
				/>
			</div>

			<FormikFieldError text={errorText} />
		</div>
	);
};

export const RichTextEditor = memo(RichTextEditorInner);
